import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box } from '@mui/material';
import palette from '@ui/palette';
import { AvailableIconTypes } from '@ui/Icon';
import { useStore } from 'react-redux';
import {
  useCanCreateProject,
  useCanMoveProject,
  useCanShowProjectLocation,
} from 'abilities/projects';
import useClipDownloader from 'components/Download/useClipDownloader';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import useProjectSections from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import { useCanLlmTranslate } from 'abilities/llm';
import useTranslateProject from 'components/Translate/hooks/useTranslateProject';
import { useEventsContext } from 'context/events/EventsContext';
import { EventName } from 'services/eventTrackingServiceTypes';
import { AppState } from 'store';
import { useAvatarContext } from 'components/avatar/context/AvatarProvider';
import { buildSectionView } from 'components/ScriptStudio/Sections/selectors';
import useDownloadLimitPrompt from 'components/Download/useDownloadLimitPrompt';
import { ActionMenuItem, ActionMenuType, Permission } from '../../ActionMenu';
import DropdownButton from '../../DropdownButton';
import { useUserContext } from '../../../context/user/UserContext';
import useDriveItemActions from '../../Drives/hooks/useDriveItemActions';
import { ProjectFragment } from '../../fragments.generated';
import { DriveItemType } from '../../Drives/types';
import { useProjectAccessAbility } from '../../../context/projectAccess/ProjectAccessContext';
import { ProjectAccessAction, ProjectAccessSubject } from './types';
import Tooltip from '../../Tooltip';
import { ProjectCardOriginPage } from './ProjectCard';
import {
  AudioFileFormat,
  Section,
  StudioProjectVersion,
} from '../../../../server/graphql/__generated.types';

export enum ProjectActionMenuVariant {
  ProjectCard = 'projectCard',
  StudioNavBar = 'studioNavbar',
}

interface ProjectActionMenuProps {
  project: ProjectFragment;
  href?: string;
  currentDriveId?: string;
  currentFolderId?: string;
  isSharedDrive?: boolean;
  variant?: ProjectActionMenuVariant;
  hasReadOnlyPermissions?: boolean;
  disableSelect?: boolean;
  pageOrigin?: ProjectCardOriginPage;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

enum ProjectMenuActions {
  Open = 'Open',
  Settings = 'Settings',
  Move = 'Move to...',
  Download = 'Download',
  Share = 'Share',
  Location = 'Go to file location',
  Translate = 'Translate',
  Delete = 'Delete',
}

const ProjectActionsIcons: Record<
  ProjectMenuActions,
  Partial<AvailableIconTypes> | undefined
> = {
  [ProjectMenuActions.Open]: 'arrowUpRight',
  [ProjectMenuActions.Settings]: 'settings02',
  [ProjectMenuActions.Move]: 'move',
  [ProjectMenuActions.Download]: 'download02',
  [ProjectMenuActions.Share]: 'share07',
  [ProjectMenuActions.Location]: 'arrowNarrowRight',
  [ProjectMenuActions.Translate]: 'globe05',
  [ProjectMenuActions.Delete]: 'trash04',
};

const ProjectActionsType: Record<ProjectMenuActions, ActionMenuType> = {
  [ProjectMenuActions.Open]: ActionMenuType.primary,
  [ProjectMenuActions.Settings]: ActionMenuType.primary,
  [ProjectMenuActions.Move]: ActionMenuType.primary,
  [ProjectMenuActions.Download]: ActionMenuType.primary,
  [ProjectMenuActions.Share]: ActionMenuType.primary,
  [ProjectMenuActions.Location]: ActionMenuType.primary,
  [ProjectMenuActions.Translate]: ActionMenuType.primary,
  [ProjectMenuActions.Delete]: ActionMenuType.destructive,
};

const useStyles = makeStyles()(theme => ({
  menu: {
    width: theme.spacing(20),
  },
  iconButton: {
    fontSize: theme.spacing(2.5),
    padding: theme.spacing(0.5),
  },
  studioLabeledButton: {
    color: palette.semantic.text.primary,
    fontSize: 'inherit',
    height: theme.spacing(3),
    minHeight: theme.spacing(3),
    padding: theme.spacing(0, 1),
    boxSizing: 'border-box',
    lineHeight: 1,
    '& span': {
      fontSize: '0.875rem', // 14px — Medium text/Regular
      lineHeight: '18px',
      fontWeight: 400,
      color: palette.semantic.text.primary,
      fontFamily: '"Assistant", sans-serif',
    },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_200}`,
      outlineOffset: -2,
    },
  },
  studioIconButton: {
    color: palette.gray_50,
    fontSize: 'inherit',
    height: theme.spacing(3),
    minHeight: theme.spacing(3),
    padding: theme.spacing(0, 1),
    boxSizing: 'border-box',
    lineHeight: 1,
    borderRadius: theme.spacing(0.75),
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_200}`,
      outlineOffset: -2,
    },
  },
}));

const ProjectActionMenu = (props: ProjectActionMenuProps) => {
  const {
    project,
    href,
    currentFolderId,
    currentDriveId,
    isSharedDrive = false,
    variant = ProjectActionMenuVariant.ProjectCard,
    hasReadOnlyPermissions = false,
    pageOrigin,
    onMouseEnter,
    onMouseLeave,
  } = props;
  const [eventTracking] = useEventsContext();
  const { data: userQuery } = useUserContext();
  const projectAccessAbility = useProjectAccessAbility();
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const { open, move, rename, share, deleteItem, openParent } =
    useDriveItemActions({});
  const { queryAvatar } = useAvatarContext();

  const [, dispatchPrompt] = usePromptsContext();
  const isDownloadBlocked = useDownloadLimitPrompt();
  const { onDownloadProjectClips, onDownloadProjectTakes } = useClipDownloader({
    onDownloadStart: () => {},
    onDownloadComplete: () => {},
  });

  const [menuOpened, setMenuOpened] = React.useState(false);
  const isScriptStudioProject =
    project.studioVersion === StudioProjectVersion.ScriptStudio ||
    project.studioVersion === StudioProjectVersion.Universal;
  const {
    sections,
    refetch: refetchSections,
    loading: sectionsLoading,
  } = useProjectSections({
    projectId: project.id,
    limit: 500,
    skip: !menuOpened || !isScriptStudioProject,
  });
  const { onTranslate, onAvatarSelectionStep } = useTranslateProject({
    project,
    pageOrigin,
    refetchSections,
  });
  const store = useStore<AppState>();

  const { classes } = useStyles();

  const showDownloadOption = projectAccessAbility.can(
    ProjectAccessAction.Download,
    ProjectAccessSubject.Project
  );

  const showSettingsOption = projectAccessAbility.can(
    ProjectAccessAction.Rename,
    ProjectAccessSubject.Project
  );
  const showShareOption = projectAccessAbility.can(
    ProjectAccessAction.EnableShare,
    ProjectAccessSubject.Project
  );
  const showDeleteOption = projectAccessAbility.can(
    ProjectAccessAction.Delete,
    ProjectAccessSubject.Project
  );

  const canMoveProject = useCanMoveProject();
  const showMoveOption =
    pageOrigin === ProjectCardOriginPage.PageResults && canMoveProject;

  const canCreateProject = useCanCreateProject();

  const canTranslateProject = useCanLlmTranslate();

  const canShowProjectLocation = useCanShowProjectLocation();
  const showGoToLocationOption =
    pageOrigin !== ProjectCardOriginPage.PageResults && canShowProjectLocation;

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };
  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const takesSampleRatesById = useMemo(() => {
    const m: Record<string, number> = {};
    sections.forEach(section => {
      const take = section.currentTake;

      if (take?.id) {
        m[take.id] = take.sampleRate ?? project.sampleRate ?? 24000;
      }
    });
    return m;
  }, [sections, project.sampleRate]);

  const totalDurationMs = useMemo(
    () =>
      sections.reduce(
        (sum: number, section: Section) =>
          sum + (section.currentTake?.duration ?? 0),
        0
      ),
    [sections]
  );

  const handleDownloadProject = () => {
    if (isDownloadBlocked(totalDurationMs)) return;

    if (userQuery?.user.settings.globalAudioFileFormatSelected) {
      if (
        project.studioVersion === StudioProjectVersion.ScriptStudio ||
        project.studioVersion === StudioProjectVersion.Universal
      ) {
        onDownloadProjectTakes({
          projectId: project.id,
          takeCount: Object.keys(takesSampleRatesById).length,
          audioFileFormat:
            userQuery?.user.settings.globalAudioFileFormat ??
            AudioFileFormat.Mp3,
          includeScriptFormats:
            userQuery?.user.settings.includeClipScriptFormats ?? [],
        });
      } else {
        onDownloadProjectClips({
          projectId: project.id,
          audioFileFormat:
            userQuery?.user.settings.globalAudioFileFormat ??
            AudioFileFormat.Mp3,
          includeScriptFormats:
            userQuery?.user.settings.includeClipScriptFormats ?? [],
        });
      }
    } else {
      const downloadType =
        project.studioVersion === StudioProjectVersion.ScriptStudio ||
        project.studioVersion === StudioProjectVersion.Universal
          ? 'takes'
          : 'clips';

      dispatchPrompt({
        type: 'OpenDownloadPrompt',
        payload: {
          downloadType,
          projectId: project.id,
          studioVersion: project.studioVersion,
          takeDownloadSource: 'bulk',
          ...(downloadType === 'takes' && takesSampleRatesById
            ? { takesSampleRatesById }
            : {}),
          projectName: project.name,
          bulkPauseLength: project.bulkPauseLength,
        },
      });
    }
  };

  const handleTranslateProject = () => {
    const buffers = store.getState().scriptStudioBuffers.byId;
    const avatarLocales = sections.map(section => {
      if (!section) return 'en';
      const { avatar: avatarId, avatarVariant: variantId } = buildSectionView(
        section,
        buffers[section.id]
      );
      const { variant: avatarVariant } = queryAvatar({ avatarId, variantId });
      return avatarVariant?.locale ?? 'en';
    });
    const hasNonEnglishSections = avatarLocales.some(
      locale => !locale.startsWith('en')
    );

    if (hasNonEnglishSections) {
      dispatchPrompt({ type: 'OpenNonEnglishTranslationWarningPrompt' });
      return;
    }
    dispatchPrompt({
      type: 'OpenTranslatePrompt',
      payload: { projectId: project.id, onTranslate, onAvatarSelectionStep },
    });
  };

  const disableMoveOption = isSharedDrive || hasReadOnlyPermissions;

  const disabledTooltip = (children: any) => {
    return (
      <Tooltip
        text={
          isSharedDrive
            ? 'Moving coming soon!'
            : `You don't have move permissions`
        }
        placement="top-start"
        open={tooltipOpen}
        onMouseEnter={handleTooltipOpen}
        onMouseLeave={handleTooltipClose}
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
      >
        {children}
      </Tooltip>
    );
  };

  const openProjectSettings = () => {
    if (isScriptStudioProject) {
      eventTracking.track({
        type: EventName.ProjectSettingsModalViewed,
        data: {
          projectId: project.id,
          source:
            variant === ProjectActionMenuVariant.ProjectCard
              ? 'projectCard'
              : 'header',
        },
      });
    }
    rename({
      type: DriveItemType.Project,
      id: project.id,
      name: project.name,
      description: project.clientName || '',
      project,
    });
  };

  const ProjectActionsPermissions: Record<ProjectMenuActions, Permission> = {
    [ProjectMenuActions.Open]: {
      show: variant === ProjectActionMenuVariant.ProjectCard,
      disabled: false,
    },
    [ProjectMenuActions.Settings]: {
      show: showSettingsOption,
      disabled: false,
    },
    [ProjectMenuActions.Move]: {
      show: showMoveOption,
      disabled:
        disableMoveOption && pageOrigin !== ProjectCardOriginPage.StudioNavbar,
      disabledTooltip: disableMoveOption ? disabledTooltip : null,
    },
    [ProjectMenuActions.Download]: {
      show: showDownloadOption,
      disabled: false,
    },
    [ProjectMenuActions.Share]: {
      show: showShareOption,
      disabled: false,
    },
    [ProjectMenuActions.Location]: {
      show: showGoToLocationOption,
      disabled: false,
    },
    [ProjectMenuActions.Translate]: {
      show:
        canCreateProject &&
        canTranslateProject &&
        project.studioVersion === StudioProjectVersion.Universal,
      disabled: sectionsLoading,
    },
    [ProjectMenuActions.Delete]: {
      show:
        showDeleteOption && variant === ProjectActionMenuVariant.ProjectCard,
      disabled: false,
    },
  };

  const reduceActions = (action: string) => {
    switch (action) {
      case ProjectMenuActions.Move:
        move({ project, currentFolderId, currentDriveId });
        break;
      case ProjectMenuActions.Open:
        open(href);
        break;
      case ProjectMenuActions.Settings:
        openProjectSettings();
        break;
      case ProjectMenuActions.Download:
        handleDownloadProject();
        break;
      case ProjectMenuActions.Share:
        share({ type: DriveItemType.Project, project });
        break;
      case ProjectMenuActions.Location:
        openParent({
          currentDriveId: project.rootDriveId || undefined,
          currentFolderId: project.folderId,
        });
        break;
      case ProjectMenuActions.Translate:
        handleTranslateProject();
        break;
      case ProjectMenuActions.Delete:
        deleteItem({ type: DriveItemType.Project, project });
        break;
      default:
        break;
    }
  };

  const filtered = Object.values(ProjectMenuActions).filter(
    a => ProjectActionsPermissions[a].show
  );

  const children: React.ReactElement[] = filtered.map(a => {
    return (
      <ActionMenuItem<typeof ProjectActionsType, typeof ProjectActionsIcons>
        key={a}
        id={a}
        action={a}
        onClick={() => reduceActions(a)}
        IconRecord={ProjectActionsIcons}
        MenuRecord={ProjectActionsType}
        disabled={ProjectActionsPermissions[a].disabled}
        disabledTooltip={ProjectActionsPermissions[a].disabledTooltip}
      />
    );
  });

  const isStudioNavBar = variant === ProjectActionMenuVariant.StudioNavBar;

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={e => e.stopPropagation()}
      onClickCapture={() => setMenuOpened(true)}
    >
      <DropdownButton
        id={project.id}
        iconType={isStudioNavBar ? undefined : 'moreHoriz'}
        variant={isStudioNavBar ? 'labeled' : 'icon'}
        label={isStudioNavBar ? 'Project' : undefined}
        classes={{
          menuContent: classes.menu,
          iconButton: isStudioNavBar
            ? classes.studioIconButton
            : classes.iconButton,
          labeledButton: isStudioNavBar
            ? classes.studioLabeledButton
            : undefined,
        }}
        iconFontSize="inherit"
        aria-label="Project actions"
      >
        {children}
      </DropdownButton>
    </Box>
  );
};

export default ProjectActionMenu;
